import AdminButton from "../common/AdminButton";

interface AdminPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  maxVisible?: number;
}

const AdminPagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  itemLabel = "건",
  maxVisible = 5,
}: AdminPaginationProps) => {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(currentPage, 1), safeTotalPages);
  const start = totalCount > 0 ? (safePage - 1) * pageSize + 1 : 0;
  const end = Math.min(safePage * pageSize, totalCount);
  const visibleCount = Math.min(maxVisible, safeTotalPages);
  const half = Math.floor(visibleCount / 2);
  const startPage = Math.max(1, Math.min(safePage - half, safeTotalPages - visibleCount + 1));
  const pages = Array.from({ length: visibleCount }, (_, index) => startPage + index);

  return (
    <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
      <span className="text-xs text-slate-400">
        총 {totalCount}
        {itemLabel} 중 {start}-{end}
        {itemLabel} 표시
      </span>
      <nav className="flex items-center gap-1.5" aria-label="페이지 이동">
        <AdminButton
          variant="ghost"
          size="sm"
          icon="ri-arrow-left-s-line"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          aria-label="이전 페이지"
        />
        {startPage > 1 && (
          <AdminButton variant={safePage === 1 ? "soft" : "ghost"} size="sm" className="min-w-7" onClick={() => onPageChange(1)} aria-current={safePage === 1 ? "page" : undefined} aria-label="1페이지">
            1
          </AdminButton>
        )}
        {startPage > 2 && <span className="px-1 text-xs text-slate-400">...</span>}
        {pages.map((page) => (
          <AdminButton
            key={page}
            variant={safePage === page ? "soft" : "ghost"}
            size="sm"
            className="min-w-7"
            onClick={() => onPageChange(page)}
            aria-current={safePage === page ? "page" : undefined}
            aria-label={`${page}페이지`}
          >
            {page}
          </AdminButton>
        ))}
        {startPage + visibleCount < safeTotalPages && <span className="px-1 text-xs text-slate-400">...</span>}
        {startPage + visibleCount <= safeTotalPages && safeTotalPages > 1 && (
          <AdminButton
            variant={safePage === safeTotalPages ? "soft" : "ghost"}
            size="sm"
            className="min-w-7"
            onClick={() => onPageChange(safeTotalPages)}
            aria-current={safePage === safeTotalPages ? "page" : undefined}
            aria-label={`${safeTotalPages}페이지`}
          >
            {safeTotalPages}
          </AdminButton>
        )}
        <AdminButton
          variant="ghost"
          size="sm"
          icon="ri-arrow-right-s-line"
          disabled={safePage >= safeTotalPages}
          onClick={() => onPageChange(safePage + 1)}
          aria-label="다음 페이지"
        />
      </nav>
    </div>
  );
};

export default AdminPagination;
